import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowCircleUp from '@mui/icons-material/ArrowCircleUp';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import LoginIcon from '@mui/icons-material/Login';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import PhoneIcon from '@mui/icons-material/Phone';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { ServiceWorkerContext } from '_lib/api/serviceWorkerContextProvider';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';

import { FlexBox, FlexBoxProps } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';

import { useServiceTitanEnterpriseLoginDialog } from '_lib/hooks/ServiceTitanEnterpriseLoginDialogHook';

import logoWhiteImage from '../../../assets/img/symbol_white.png';

import { Divider } from '../../components/Divider/Divider';
import { Drawer } from '../../components/Drawer/Drawer';
import { IconButton } from '../../components/IconButton/IconButton';
import { List } from '../../components/List/List';
import { Typography } from '../../components/Typography/Typography';

import { VERSION_DISPLAY } from '../../global/config';
import { useWindowSize } from '../../hooks/useWindowResize';
import { AiCallIcon } from '../AiCall/AiCallIcon';
import { PermissionFunctionArgs } from '../auth/auth.types';
import { AuthContext } from '../auth/authContext';
import {
  CallTakingEnabledForAccount,
  ManageAccountUsers,
  ManageCallCenterUsers,
  ManageCompanies,
  SystemUser,
  ViewAccounts,
  ViewAccountUsers,
  ViewCallCenterUsers
} from '../auth/permissionHelpers';
import { appRoutePaths } from '../Nav/nav.types';
import { NavigationItem } from '../Nav/NavigationItem';

import { RouterNavigationItem } from '../Nav/RouterNavigationItem';

import { AppDrawerState } from './app.types';

const drawerSx = { maxHeight: '100%', display: 'flex', flexDirection: 'column' };
const DRAWER_WIDTH = 240;
const MD_BREAKPOINT = 900;

const HeaderLogo = (props?: { styles?: React.HTMLAttributes<HTMLImageElement>['style'] }) => {
  return (
    <img
      src={logoWhiteImage}
      alt='Logo'
      style={{ maxHeight: '24px', maxWidth: '24px', display: 'block', ...props?.styles }}
    />
  );
};

interface DrawerHeaderProps extends FlexBoxProps {
  open: boolean;
}

const DrawerHeader = (props: DrawerHeaderProps) => <FlexBox {...props} />;

const AppDrawerHeader = styled(DrawerHeader, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  backgroundColor: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: open ? 'space-between' : 'center',
  padding: theme.spacing(0, 1),
  boxShadow: theme.shadows[4],
  // use the following if we use toolbar and want consistent toolbar styles applied
  ...theme.mixins.toolbar
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('md')]: {
    width: `calc(${theme.spacing(8)} + 1px)`
  }
});

const AppDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}));

const SlidingAppDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...{
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }
}));

const navItems = [
  {
    icon: <PhoneIcon />,
    routePath: appRoutePaths.CallLog,
    title: 'Call log',
    routeAllowed: (args: PermissionFunctionArgs) => CallTakingEnabledForAccount(args)
  },
  {
    icon: <AiCallIcon />,
    routePath: appRoutePaths.AiCall,
    title: 'CPI Call log',
    routeAllowed: (args: PermissionFunctionArgs) => CallTakingEnabledForAccount(args)
  },
  {
    icon: <HolidayVillageIcon />,
    routePath: appRoutePaths.Account,
    title: 'Accounts',
    routeAllowed: ViewAccounts
  },
  {
    icon: <BusinessIcon />,
    routePath: appRoutePaths.Company,
    title: 'Companies',
    routeAllowed: ManageCompanies
  },
  // NOTE - do we want to call both of these 'Users'? Or differentiate between Account Users and Call Center Users?
  {
    icon: <PeopleIcon />,
    routePath: appRoutePaths.AccountUser,
    title: 'Users',
    routeAllowed: (args: PermissionFunctionArgs) => ViewAccountUsers(args) && ManageAccountUsers(args)
  },
  {
    icon: <PeopleIcon />,
    routePath: appRoutePaths.User,
    title: 'Users',
    routeAllowed: (args: PermissionFunctionArgs) => ViewCallCenterUsers(args) && ManageCallCenterUsers(args)
  },
  {
    icon: <AdminPanelSettingsIcon />,
    routePath: appRoutePaths.SystemAdministration,
    title: 'System Administration',
    routeAllowed: SystemUser
  }
];

// NOTE - since this is used to generate JSX, it need to be synchronous
const getNavListItems = (args: PermissionFunctionArgs) =>
  navItems.filter((navItem) => (navItem.routeAllowed ? navItem.routeAllowed(args) : true));

export const AppRoot = (): React.ReactElement => {
  const { authUserCacheResource, accountCacheResource, callCenterCacheResource } = useContext(AuthContext);

  const cachedAccountQuery = accountCacheResource.useCacheQuery();
  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  const { isUpdateAvailable, updateApp, isUpdateInProgress } = useContext(ServiceWorkerContext);

  const api = useContext(ApiContext);

  const healthCheckQuery = api.appConfig.useHealthCheckQuery();

  const stEnterpriseLoginDialog = useServiceTitanEnterpriseLoginDialog();

  const [isDrawerExpanded, setIsDrawerExpanded] = useState(true);
  const [isSlidingDrawerOpen, setIsSlidingDrawerOpen] = useState(false);

  const isDrawerExpandedOrOpen = useMemo<boolean>(
    () => isDrawerExpanded || isSlidingDrawerOpen,
    [isDrawerExpanded, isSlidingDrawerOpen]
  );

  const setIsDrawerExpandedOrOpen = useCallback((val: boolean) => {
    setIsDrawerExpanded(val);
    setIsSlidingDrawerOpen(val);
  }, []);

  const appDrawerState = useMemo<AppDrawerState>(
    () => ({
      closeSlidingDrawer: () => setIsSlidingDrawerOpen(false),
      openSlidingDrawer: () => setIsSlidingDrawerOpen(true)
    }),
    []
  );

  const windowSize = useWindowSize();

  useEffect(() => {
    if (windowSize.width > MD_BREAKPOINT) {
      setIsSlidingDrawerOpen(false);
    }
  }, [windowSize.width]);

  const drawerContents = (
    <>
      <AppDrawerHeader open={isDrawerExpandedOrOpen}>
        {isDrawerExpandedOrOpen && <HeaderLogo styles={{ marginLeft: 1 }} />}
        <IconButton color='primaryInverted' onClick={() => setIsDrawerExpandedOrOpen(!isDrawerExpandedOrOpen)}>
          {isDrawerExpandedOrOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </AppDrawerHeader>
      {/* TODO: Need to be able to compose (inject) Non navigation items. Likely need isSticky or isScrollable? */}
      {/* TODO: on successful navigation, we should close the drawer */}
      <List sx={{ paddingTop: 0, display: 'flex', flexDirection: 'column', overflowY: 'scroll' }}>
        {cachedAuthUserQuery?.data &&
          cachedCallCenterQuery.data &&
          getNavListItems({ user: cachedAuthUserQuery?.data, account: cachedAccountQuery.data }).map((navItem) => (
            <RouterNavigationItem
              isNavExpanded={isDrawerExpandedOrOpen}
              key={navItem.routePath}
              icon={navItem.icon}
              text={navItem.title}
              routePath={navItem.routePath}
              setIsSlidingDrawerOpen={setIsSlidingDrawerOpen}
            />
          ))}
        {cachedAuthUserQuery?.data && SystemUser({ user: cachedAuthUserQuery.data }) && (
          <RouterNavigationItem
            isNavExpanded={isDrawerExpandedOrOpen}
            icon={<HeadsetMicIcon />}
            text='Call Centers'
            routePath={appRoutePaths.CallCenter}
            setIsSlidingDrawerOpen={setIsSlidingDrawerOpen}
          />
        )}
      </List>
      {/* TODO - Need to be able to compose (inject) Non navigation after nav items. Likely need isSticky or isScrollable? */}
      <FlexBox sx={{ marginTop: 'auto', flexDirection: 'column' }}>
        {isUpdateAvailable && (
          <>
            <Divider />
            <NavigationItem
              isNavExpanded={isDrawerExpandedOrOpen}
              icon={<ArrowCircleUp />}
              text='Update Now'
              onClick={updateApp}
            />
          </>
        )}
        {isDrawerExpandedOrOpen && (
          <FlexBox sx={{ flexDirection: 'column', width: '100%' }}>
            <Divider />
            <Typography variant='body2' sx={{ paddingY: 1, paddingLeft: 1 }}>
              API Status:{' '}
              {healthCheckQuery?.data && (
                <Typography
                  variant='body2'
                  component='span'
                  text={healthCheckQuery.data.isFailover ? 'failover' : 'primary'}
                  color={healthCheckQuery.data.isFailover ? 'warning' : 'success'}
                />
              )}
            </Typography>
            <Divider />
            <FlexBox sx={{ paddingY: 1, paddingLeft: 1, flexDirection: 'row', alignItems: 'center' }}>
              <Typography text={VERSION_DISPLAY} variant='subtitle2Italic' />
              <Stack useFlexGap direction='row' spacing={0} sx={{ marginLeft: 'auto', marginRight: 1 }}>
                <Tooltip title='Service Titan Enterprise Login' placement='top'>
                  <IconButton size='small' color='primary' onClick={() => stEnterpriseLoginDialog.show({})}>
                    <LoginIcon />
                  </IconButton>
                </Tooltip>
                {/* <IconButton size='small' color='primary' onClick={updateApp}>
                  <OfflineBoltIcon />
                </IconButton> */}
              </Stack>
            </FlexBox>
          </FlexBox>
        )}
      </FlexBox>
      {stEnterpriseLoginDialog.element}
    </>
  );

  return (
    <FlexBox sx={{ flexDirection: 'row', height: '100%', maxHeight: '100%', maxWidth: '100%' }}>
      <BackdropSpinner open={isUpdateInProgress} />
      <SlidingAppDrawer
        variant='temporary'
        open={isSlidingDrawerOpen}
        onClose={() => setIsSlidingDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={(theme) => ({
          ...drawerSx,
          [theme.breakpoints.down('md')]: { display: 'block' },
          [theme.breakpoints.up('md')]: { display: 'none' }
        })}>
        {drawerContents}
      </SlidingAppDrawer>
      <AppDrawer
        variant='permanent'
        open={isDrawerExpanded}
        onClose={() => setIsDrawerExpanded(false)}
        sx={(theme) => ({
          ...drawerSx,
          [theme.breakpoints.down('md')]: { display: 'none' },
          [theme.breakpoints.up('md')]: { display: 'block' }
        })}>
        {drawerContents}
      </AppDrawer>
      <Outlet context={{ appDrawerState }} />
    </FlexBox>
  );
};
